import React from 'react';
import { alpha, Box, Checkbox, Chip, FormControlLabel, Stack, Typography } from '@mui/material';
import { FormComponentValue } from 'components/common/form/layout/control';

export type AiInsightsListProps = {
    values: FormComponentValue[];
    mode: 'all' | 'selected';
    checkedSet?: string[];
    onToggle?: (value: string, checked: boolean) => void;
    conditionalChipLabel: string;
};

export default function AiInsightsList({
    values,
    mode,
    checkedSet = [],
    onToggle,
    conditionalChipLabel,
}: AiInsightsListProps) {
    return (
        <Stack
            sx={{
                border: '1px solid',
                borderColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                borderRadius: 1,
            }}
        >
            {values.map((item) => {
                const isChecked = mode === 'selected' && checkedSet.includes(item.value);
                return (
                    <Stack
                        direction={'row'}
                        key={item.value}
                        className={`view--${mode} ${isChecked ? 'checked' : ''}`}
                        sx={{
                            borderTop: '1px solid',
                            borderTopColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                            px: 1.5,
                            py: 1,
                            position: 'relative',
                            overflow: 'hidden',
                            '&.view--selected': {
                                pl: 0.5,
                                '&:hover': {
                                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                                    cursor: 'pointer',
                                },
                            },
                            '&:first-of-type': {
                                borderTop: 0,
                            },
                            '&.checked': {
                                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                '&:before': {
                                    content: '""',
                                    width: '1px',
                                    height: '100%',
                                    backgroundColor: (theme) => theme.palette.primary.main,
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                },
                            },
                        }}
                        onClick={() => {
                            if (mode === 'selected') onToggle?.(item.value, !isChecked);
                        }}
                    >
                        {mode === 'selected' && (
                            <Stack direction={'row'} alignItems={'center'} sx={{ flexShrink: 0 }}>
                                <Checkbox checked={isChecked} />
                            </Stack>
                        )}
                        <Box sx={{ flexGrow: 1, wordWrap: 'break-word' }}>
                            <Box>
                                <Box component="span" sx={{ mr: 1 }}>
                                    {item.label}
                                </Box>
                                {item.props?.isConditional && <ConditionalChip label={conditionalChipLabel} />}
                            </Box>
                            {item.props?.desc && (
                                <Box sx={{ color: (theme) => alpha(theme.palette.text.primary, 0.64), pt: 0.25 }}>
                                    {item.props.desc}
                                </Box>
                            )}
                        </Box>
                    </Stack>
                );
            })}
        </Stack>
    );
}

function ConditionalChip({ label }: { label: string }) {
    return <Chip label={label} color={'primary'} sx={{ textTransform: 'uppercase' }} />;
}
